import React from 'react'
import { DataTableProvider } from 'components/DataTable'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import { ContactCompany } from 'models/companies'
import apiService from 'services/api'

interface SingleItemAssignModalProps {
  show: boolean
  handleClose: () => void
  contactCompanyId?: number // Company ID for the parent company
  doorProxyId?: number // Door proxy ID for door pages API operations
  contactCompany: Partial<ContactCompany>
  onSuccess?: () => void
  isDoorPage?: boolean // New prop to indicate if this is a door page
}

// Wrapper component that provides DataTableContext for assignment modals
function SingleItemDataTableWrapper({
  children,
  tableKey,
}: {
  children: React.ReactNode
  tableKey: string
}) {
  return <DataTableProvider tableKey={tableKey}>{children}</DataTableProvider>
}

// Single Item Tag Assignment Modal
export function SingleItemTagAssignModal({
  show,
  handleClose,
  contactCompanyId,
  doorProxyId,
  contactCompany,
  onSuccess,
  isDoorPage = false,
}: SingleItemAssignModalProps) {
  const api = apiService()

  // Create mock table data for a single company/door
  const tableData = [contactCompany]
  const identifier = isDoorPage ? doorProxyId : contactCompanyId
  const tableQueryKey = [
    isDoorPage
      ? `door-tags-${doorProxyId}`
      : `company-tags-${contactCompanyId}`,
  ]
  const tableKey = `single-item-tags-${identifier}`

  const handleCloseWithSuccess = () => {
    onSuccess?.()
    handleClose()
  }

  return (
    <SingleItemDataTableWrapper tableKey={tableKey}>
      <TagAssignModal
        show={show}
        handleClose={handleCloseWithSuccess}
        tableQueryKey={tableQueryKey}
        data={tableData}
        idAccessor="id"
        tableObjAccessor={isDoorPage ? 'door_taglist' : 'taglist'}
        filterAndSortParams={{}}
        apiTagsAction={(values) => {
          // For single-item operations, we need to set include_ids to the specific company/door
          const modifiedValues = {
            ...values,
            include_ids: isDoorPage ? [doorProxyId!] : [contactCompanyId!], // For doors, use doorProxyId in include_ids
            exclude_ids: [],
          }
          return api.commonManageTags({
            values: modifiedValues,
            tableType: isDoorPage
              ? 'restaurants-bars-doors'
              : 'companies-table',
          })
        }}
      />
    </SingleItemDataTableWrapper>
  )
}

// Single Item Distributor Assignment Modal
export function SingleItemDistributorAssignModal({
  show,
  handleClose,
  contactCompanyId,
  doorProxyId,
  contactCompany,
  onSuccess,
  isDoorPage = false,
}: SingleItemAssignModalProps) {
  const api = apiService()

  const tableData = [contactCompany]
  const identifier = isDoorPage ? doorProxyId : contactCompanyId
  const tableQueryKey = [
    isDoorPage
      ? `door-distributors-${doorProxyId}`
      : `company-distributors-${contactCompanyId}`,
  ]
  const tableKey = `single-item-distributors-${identifier}`

  const handleCloseWithSuccess = () => {
    onSuccess?.()
    handleClose()
  }

  return (
    <SingleItemDataTableWrapper tableKey={tableKey}>
      <DistributorAssignModal
        show={show}
        handleClose={handleCloseWithSuccess}
        tableQueryKey={tableQueryKey}
        data={tableData}
        idAccessor="id"
        tableObjAccessor={isDoorPage ? 'door_distributors' : 'distributors'}
        filterAndSortParams={{}}
        apiDistributorsAction={(values) => {
          // For single-item operations, we need to set include_ids to the specific company/door
          const modifiedValues = {
            ...values,
            include_ids: isDoorPage ? [doorProxyId!] : [contactCompanyId!], // For doors, use doorProxyId in include_ids
            exclude_ids: [],
          }
          return api.commonManageDistributors({
            tableType: isDoorPage
              ? 'restaurants-bars-doors'
              : 'companies-table',
            values: modifiedValues,
          })
        }}
      />
    </SingleItemDataTableWrapper>
  )
}

// Single Item GPO Assignment Modal
export function SingleItemGPOAssignModal({
  show,
  handleClose,
  contactCompanyId,
  doorProxyId,
  contactCompany,
  onSuccess,
  isDoorPage = false,
}: SingleItemAssignModalProps) {
  const api = apiService()

  const tableData = [contactCompany]
  const identifier = isDoorPage ? doorProxyId : contactCompanyId
  const tableQueryKey = [
    isDoorPage
      ? `door-gpos-${doorProxyId}`
      : `company-gpos-${contactCompanyId}`,
  ]
  const tableKey = `single-item-gpos-${identifier}`

  const handleCloseWithSuccess = () => {
    onSuccess?.()
    handleClose()
  }

  return (
    <SingleItemDataTableWrapper tableKey={tableKey}>
      <GPOAssignModal
        show={show}
        handleClose={handleCloseWithSuccess}
        tableQueryKey={tableQueryKey}
        data={tableData}
        idAccessor="id"
        tableObjAccessor={isDoorPage ? 'door_gpos' : 'gpos'}
        filterAndSortParams={{}}
        apiGPOsAction={(values) => {
          // For single-item operations, we need to set include_ids to the specific company/door
          const modifiedValues = {
            ...values,
            include_ids: isDoorPage ? [doorProxyId!] : [contactCompanyId!], // For doors, use doorProxyId in include_ids
            exclude_ids: [],
          }
          return api.commonManageGPOs({
            tableType: isDoorPage
              ? 'restaurants-bars-doors'
              : 'companies-table',
            values: modifiedValues,
          })
        }}
      />
    </SingleItemDataTableWrapper>
  )
}
